import { MagnifyingGlass } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import React, { ChangeEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Chip } from 'common/components/Chip';
import useDebounceString from 'common/hooks/useDebounceString';

import AccessResults from './AccessResults';
import { EntityResults } from './EntityResults';
import { useSearch } from './hooks/useSearch';
import { EntityTypeFilter, EntityTypeFilterType } from './types';

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  width: 100%;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
`;

const StyledSearchInput = styled(Input)`
  width: 100%;
`;

const MINIMUM_SEARCH_LEN = 3;
const isValidSearchQuery = (input: string) => input.length >= MINIMUM_SEARCH_LEN;

export const Search = ({ onSelect }: { onSelect?: () => void }) => {
  const [activeEntityTypeFilter, setActiveEntityTypeFilter] = useState<EntityTypeFilterType>(EntityTypeFilter.ALL);
  const { formatMessage } = useIntl();
  const EntityTypeFilterText: Record<EntityTypeFilterType, string> = {
    all: formatMessage({ id: 'experience.search.filter.all' }),
    venue: formatMessage({ id: 'experience.venues' }),
    merchant: formatMessage({ id: 'experience.merchants' }),
    brand: formatMessage({ id: 'experience.brands' }),
    enterprise: formatMessage({ id: 'experience.enterprises' }),
  };

  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebounceString(searchString);
  const isSearchEnabled = isValidSearchQuery(debouncedSearchString);

  const {
    brands,
    enterprises,
    isLoading: isSearching,
    merchants,
    venues,
  } = useSearch(debouncedSearchString, isSearchEnabled);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => setSearchString(e.target.value);

  return (
    <SearchContainer>
      <StyledSearchInput
        icon={<MagnifyingGlass />}
        placeholder={formatMessage({ id: 'merchantSearch.searchForMerchants' })}
        type="text"
        value={searchString}
        onChange={handleSearch}
      />
      <ChipsContainer>
        {Object.values(EntityTypeFilter).map(entityTypeFilter => (
          <Chip
            key={entityTypeFilter}
            isActive={entityTypeFilter === activeEntityTypeFilter}
            onClick={() => setActiveEntityTypeFilter(entityTypeFilter)}
            label={EntityTypeFilterText[entityTypeFilter]}
          />
        ))}
      </ChipsContainer>
      {isSearchEnabled ? (
        <EntityResults
          brands={brands}
          enterprises={enterprises}
          isLoading={isSearching}
          merchants={merchants}
          venues={venues}
          filter={activeEntityTypeFilter}
          onSelect={onSelect}
        />
      ) : (
        <AccessResults filter={activeEntityTypeFilter} onSelect={onSelect} />
      )}
    </SearchContainer>
  );
};
